import { type ClassificationContent, type ClassificationResult, ContentType } from '@classification-testing/domain';
import { useClassifyWithUpload, useClassifyWithoutUpload } from '@classification-testing/hooks';
import { selectCommunityResultHistory } from '@classification-testing/state/selectors';
import { track } from '@core/logging';
import { useAppSelector } from '@store';
import { useCallback } from 'react';

/**
 * Main hook for content classification that handles both direct content and file uploads
 * Internally routes to appropriate existing hooks based on content type
 *
 * This approach maintains the existing, tested functionality while providing a unified interface
 */
export function useClassifyContent(communityId?: string, promptVersion?: string) {
  // Use existing hooks for their proven functionality
  const directClassification = useClassifyWithoutUpload(communityId, promptVersion);
  const uploadClassification = useClassifyWithUpload(communityId, promptVersion);

  // Get result history from Redux
  const resultHistory = useAppSelector(communityId ? selectCommunityResultHistory(communityId) : () => []);

  // Helper function to handle blob URL classification
  const classifyWithBlobUrl = useCallback(
    async (content: ClassificationContent): Promise<ClassificationResult> => {
      if (content.type === ContentType.AUDIO && content.audioBlobUrl) {
        return await directClassification.classifyContent({
          type: ContentType.AUDIO,
          audioName: content.audioBlobUrl,
        });
      } else if (content.type === ContentType.IMAGE && content.imageBlobUrl) {
        return await directClassification.classifyContent({
          type: ContentType.IMAGE,
          imageName: content.imageBlobUrl,
        });
      }

      throw new Error('Invalid blob URL content type for classification');
    },
    [directClassification]
  );

  // Unified classification function that routes internally
  const classifyContent = useCallback(
    // eslint-disable-next-line complexity
    async (content: ClassificationContent | File): Promise<ClassificationResult> => {
      track('ClassifyContentInitiated', {
        communityId: communityId || 'unknown',
        contentType: content instanceof File ? 'file' : content.type,
        fileType: content instanceof File ? content.type : 'none',
        promptVersion: promptVersion || 'default',
      });

      // Route bvased on input type
      if (content instanceof File) {
        // File upload path - now uses two-step process with Media Service
        return await uploadClassification.classifyWithUpload(content);
      } else if (
        content.type === ContentType.TEXT ||
        (content.type === ContentType.AUDIO && content.audioName) ||
        (content.type === ContentType.IMAGE && content.imageName)
      ) {
        // Direct content path (text, audio names, image names)
        return await directClassification.classifyContent(content);
      } else if ((content.type === ContentType.AUDIO && content.audioBlobUrl) || (content.type === ContentType.IMAGE && content.imageBlobUrl)) {
        // Blob URL path
        return await classifyWithBlobUrl(content);
      } else {
        throw new Error('Invalid content type for classification');
      }
    },
    [communityId, promptVersion, uploadClassification, directClassification, classifyWithBlobUrl]
  );

  // Unified loading state (true if either hook is loading)
  const isLoading = directClassification.isLoading || uploadClassification.loading;

  // Unified error state (prioritize direct classification errors, fallback to upload errors)
  const errors = directClassification.errors || uploadClassification.errors;

  return {
    classifyContent,
    resultHistory,
    isLoading,
    errors,
    // Expose individual hook methods for advanced use cases
    classifyDirectContent: directClassification.classifyContent,
    classifyFileContent: uploadClassification.classifyWithUpload,
  };
}

/**
 * Type guard to check if content is a File
 */
export function isFile(content: ClassificationContent | File): content is File {
  return content instanceof File;
}

/**
 * Type guard to check if content is direct classification content
 */
export function isDirectContent(content: ClassificationContent | File): content is ClassificationContent {
  return !isFile(content);
}

/**
 * Helper function to determine if content requires file upload
 */
export function requiresFileUpload(content: ClassificationContent | File): boolean {
  if (isFile(content)) {
    return true;
  }

  // Direct content doesn't require file upload if it has text, audioName, or imageName
  return !(content.type === 'text' || (content.type === 'audio' && content.audioName) || (content.type === 'image' && content.imageName));
}
